/**
 * Client-side error tracker stub.
 *
 * TODO(refactor-debt-004): swap to Sentry/GlitchTip SDK when onboarding first customer.
 * See REFACTOR-DEBT.md#refactor-debt-004 for swap plan.
 *
 * Current implementation: `console.error` with structured context.
 * All application code should import `captureException` from here (never call
 * `console.error` directly for unexpected errors) so the swap is a one-file edit.
 */

export type ErrorContext = Readonly<Record<string, unknown>>;

/**
 * Initialise the tracker. No-op today; becomes `Sentry.init(...)` later.
 * Safe to call multiple times.
 */
export function initErrorTracker(): void {
  // TODO(refactor-debt-004): wire up Sentry.init / GlitchTip SDK here.
  // Intentionally empty while we are pre-customer.
}

/**
 * Record an unexpected error. Accepts unknown values (React error boundaries,
 * promise rejections, fetch failures, etc.) and normalises to something loggable.
 */
export function captureException(err: unknown, context?: ErrorContext): void {
  const normalised = normaliseError(err);
  // TODO(refactor-debt-004): replace with `Sentry.captureException(err, { extra: context })`.

  console.error('[error-tracker]', normalised, context ?? {});
}

function normaliseError(err: unknown): { name: string; message: string; stack?: string } {
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      ...(err.stack !== undefined ? { stack: err.stack } : {}),
    };
  }
  if (typeof err === 'string') {
    return { name: 'StringError', message: err };
  }
  try {
    return { name: 'UnknownError', message: JSON.stringify(err) };
  } catch {
    return { name: 'UnknownError', message: String(err) };
  }
}
